/**
 * BlockedFieldCallout (S1.3) — net-new shared component (holistic plan list).
 *
 * Renders a CLARIFY field slot per the house-board field-state contract:
 * Blocked = purple, NON-editable, "no guess"「等待決定」. It replaces the input
 * a field would otherwise render — WONT fields render nothing at all
 * (hide-don't-disable applies to phases, Blocked is its own visible state so
 * users know the field exists but awaits a decision).
 *
 * All user-visible text is injected via props — consumers translate with their
 * own namespace (keys in BOTH en + zh). No literals, no namespace ownership
 * inside common/ (WizardShell texts-prop precedent).
 */

import { ReactNode } from 'react';
import { Tag, Typography, theme } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

export interface BlockedFieldCalloutProps {
  /** Localized field label (rendered where the input label would sit). */
  label: string;
  /** Localized blocked-state chip, e.g. 等待決定 / Pending decision. */
  statusText: string;
  /** Optional localized one-liner (who owns the pending decision, etc.). */
  description?: ReactNode;
  /** Extra class for grid placement. */
  className?: string;
}

/**
 * Purple non-editable placeholder for a Blocked-CLARIFY field.
 */
export const BlockedFieldCallout = ({
  label,
  statusText,
  description,
  className,
}: BlockedFieldCalloutProps) => {
  const { token } = theme.useToken();

  return (
    <div className={className}>
      <Text
        style={{
          fontSize: token.fontSizeSM,
          color: token.colorTextSecondary,
          marginBottom: '4px',
          display: 'block',
        }}
      >
        {label}
      </Text>
      <div
        aria-disabled="true"
        style={{
          minHeight: `${controlHeightTokens.controlHeightXL}px`,
          borderRadius: `${radiusTokens.card}px`,
          border: `1px dashed ${token.colorPrimaryBorder}`,
          backgroundColor: token.colorPrimaryBg,
          display: 'flex',
          alignItems: 'center',
          gap: token.marginXS,
          padding: `0 ${token.paddingSM}px`,
          cursor: 'not-allowed',
        }}
      >
        <LockOutlined style={{ color: token.colorPrimary, fontSize: token.fontSize }} />
        <Tag color="purple" style={{ marginInlineEnd: 0 }}>
          {statusText}
        </Tag>
      </div>
      {description !== undefined && (
        <Text
          style={{
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            marginTop: '4px',
            display: 'block',
          }}
        >
          {description}
        </Text>
      )}
    </div>
  );
};

export default BlockedFieldCallout;
